import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from "react";
import { useSelector } from "react-redux";
import backendVerifiedUser from "../api/backendVerifiedUser";
import { permissionForPath, LANDING_ORDER } from "./routePermissions";

// Current admin's role permissions (GET /admin/my-permissions). The backend
// enforces the same rules on every API; this is only for showing/hiding UI.
const PermissionContext = createContext({
  loading: true,
  isSuper: false,
  hasAny: false,
  roleName: null,
  can: () => false,
  canOpen: () => false,
  firstAllowedPath: () => null,
  refresh: () => {},
});

export function PermissionProvider({ children }) {
  const { token } = useSelector((state) => state.AuthReducer);
  const [state, setState] = useState({ loading: !!token, data: null });

  const load = useCallback(async () => {
    if (!token) {
      setState({ loading: false, data: null });
      return;
    }
    setState((s) => ({ ...s, loading: true }));
    try {
      const res = await backendVerifiedUser.get("/my-permissions");
      setState({ loading: false, data: res.data });
    } catch (e) {
      setState({ loading: false, data: null });
    }
  }, [token]);

  useEffect(() => {
    load();
  }, [load]);

  const value = useMemo(() => {
    const data = state.data;
    const can = (module, action = "view") => {
      if (!data) return false;
      if (data.isSuper) return true;
      return !!data.permissions?.[module]?.[action];
    };
    const canOpen = (pathname) => {
      const need = permissionForPath(pathname);
      return need ? can(need.module, need.action) : !!data; // unmapped pages: any logged-in admin
    };
    return {
      loading: state.loading,
      isSuper: !!data?.isSuper,
      hasAny: !!data?.hasAny,
      roleName: data?.roleName || null,
      can,
      canOpen,
      firstAllowedPath: () => LANDING_ORDER.find((p) => canOpen(p)) || null,
      refresh: load,
    };
  }, [state, load]);

  return <PermissionContext.Provider value={value}>{children}</PermissionContext.Provider>;
}

export const usePermissions = () => useContext(PermissionContext);
